import { useRef, useState } from 'react'
import { FEATURE_LABELS, featureLabel } from '../lib/format'

interface Props {
  value: string[]
  label: string
  onChange: (next: string[]) => void
}

/** Compact multi-select for kenmerken: a button that opens a checkbox list, closable with Escape. */
export default function FeaturePicker({ value, label, onChange }: Props) {
  const [open, setOpen] = useState(false)
  const button = useRef<HTMLButtonElement>(null)
  // Known kenmerken plus anything this park already uses, so nothing is silently dropped.
  const keys = [...new Set([...Object.keys(FEATURE_LABELS), ...value])]
  // The column is narrow, so the button counts and the full list lives in the tooltip and the panel.
  const summary = value.length === 0 ? 'geen kenmerken' : value.map(featureLabel).join(', ')

  const toggle = (key: string, on: boolean) => {
    onChange(on ? [...value, key] : value.filter((item) => item !== key))
  }

  return (
    <div
      className="picker"
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false)
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && open) {
          setOpen(false)
          button.current?.focus()
        }
      }}
    >
      <button
        type="button"
        ref={button}
        className="btn small"
        aria-expanded={open}
        aria-label={`${label}: ${summary}`}
        title={summary}
        onClick={() => setOpen((current) => !current)}
      >
        <span className="nowrap">{value.length === 0 ? 'geen' : `${value.length} kenmerk${value.length === 1 ? '' : 'en'}`}</span>
        <span aria-hidden="true">▾</span>
      </button>
      {open && (
        <div className="picker-panel">
          {keys.map((key) => (
            <label key={key}>
              <input
                type="checkbox"
                checked={value.includes(key)}
                onChange={(event) => toggle(key, event.target.checked)}
              />
              {featureLabel(key)}
            </label>
          ))}
        </div>
      )}
    </div>
  )
}
